import { useEffect, useState } from "react";
import { AppHeader, BottomNav } from "@/components/app-header";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/store/authStore";
import { paymentsApi, WalletResponse } from "@/lib/api";
import { toast } from "sonner";
import {
  Wallet as WalletIcon,
  ArrowUpRight,
  ArrowDownRight,
  Plus,
  CreditCard,
  Lock,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2
} from "lucide-react";
import { usePaystackPayment } from "react-paystack";

export default function WalletPage() {
  const { user } = useAuthStore();
  const [data, setData] = useState<WalletResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paystackConfig, setPaystackConfig] = useState<any>(null);

  const initializePayment = usePaystackPayment({
    reference: paystackConfig?.reference || "",
    email: "payments@yenko.com", // Or user.phone + "@yenko.com" if email implies identity
    amount: paystackConfig?.amount || 0,
    publicKey: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY || "",
    currency: "GHS",
  });

  // Effect to automatically trigger the payment popup when the config updates securely from the backend
  useEffect(() => {
    if (paystackConfig && paystackConfig.reference) {
      toast.dismiss();
      initializePayment({
        onSuccess: () => {
           // Handle successful completion internally
           verifyPaymentStatus(paystackConfig.reference);
           setPaystackConfig(null);
        },
        onClose: () => {
           // Handle user close
           toast.error("Payment cancelled or closed");
           setIsProcessing(false);
           setPaystackConfig(null);
        }
      });
    }
  }, [paystackConfig]);

  useEffect(() => {
    // Check if we just returned from a Paystack checkout
    const urlParams = new URLSearchParams(window.location.search);
    const reference = urlParams.get('reference');

    if (reference) {
      verifyPaymentStatus(reference);
    } else {
      fetchWallet();
    }
  }, []);

  const verifyPaymentStatus = async (reference: string) => {
    try {
      setLoading(true);
      await paymentsApi.verifyTopup(reference);
      toast.success("Payment verified successfully!");
      
      // Clean up URL to hide query params
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error: any) {
      toast.error(error.message || "Failed to verify transaction");
    } finally {
      fetchWallet();
    }
  };

  const fetchWallet = async () => {
    try {
      const res = await paymentsApi.getWallet();
      setData(res);
    } catch (error: any) {
      toast.error(error.message || "Failed to load wallet");
    } finally {
      setLoading(false);
    }
  };

  const handleTopUp = async () => {
    const amountStr = window.prompt("Enter amount to Top-Up (GHS):", "50");
    if (!amountStr) return;
    
    const amount = parseFloat(amountStr);
    if (isNaN(amount) || amount < 1) {
      toast.error("Invalid amount");
      return;
    }

    try {
      setIsProcessing(true);
      // amount is sent in pesewas
      const payloadAmount = amount * 100;
      const res: any = await paymentsApi.topup(payloadAmount);
      
      if (res.reference) {
        toast.loading("Generating secure token...", { duration: 2000 });
        // Set the state which fires the useEffect to open Paystack iframe!
        setPaystackConfig({ reference: res.reference, amount: payloadAmount });
      } else {
        toast.error("Failed to generate payment token securely.");
        setIsProcessing(false);
      }

    } catch (error: any) {
      toast.error(error.message || "Top-up failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleWithdraw = async () => {
    if (!data || data.wallet.balance < 10) {
      toast.error("Minimum withdrawal is ₵10");
      return;
    }

    const number = window.prompt("Enter MTN Mobile Money number:", "0540000000");
    if (!number) return;

    try {
      setIsProcessing(true);
      const res = await paymentsApi.payout({
        amount: data.wallet.balance,
        momo_number: number,
        momo_network: "MTN"
      });
      toast.success(res.message);
      fetchWallet();
    } catch (error: any) {
      toast.error(error.message || "Withdrawal failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'deposit': return <ArrowDownRight className="w-5 h-5 text-green-500" />;
      case 'withdrawal': return <ArrowUpRight className="w-5 h-5 text-red-500" />;
      case 'payout': return <ArrowUpRight className="w-5 h-5 text-red-500" />;
      case 'escrow_hold': return <Lock className="w-5 h-5 text-amber-500" />;
      case 'escrow_release': return <ArrowUpRight className="w-5 h-5 text-amber-500" />;
      default: return <CreditCard className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'pending': return <Clock className="w-4 h-4 text-amber-500" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pb-20 md:pb-0">
      <AppHeader title="Wallet" />

      {loading ? (
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-12 h-12 border-3 border-yenko-blue/30 border-t-yenko-blue rounded-full animate-spin" />
        </div>
      ) : !data ? (
        <div className="text-center p-6 text-gray-500">Failed to load wallet.</div>
      ) : (
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
          {/* Balance Card */}
          <div className="bg-gradient-to-br from-yenko-blue via-blue-600 to-indigo-700 rounded-3xl p-6 text-white relative overflow-hidden shadow-xl shadow-blue-500/20">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/3 -translate-x-1/3" />

            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-2 text-white/80">
                <WalletIcon className="w-5 h-5" />
                <span className="font-medium">Available Balance</span>
              </div>
              <h1 className="text-5xl font-bold mb-4">
                ₵{Number(data.wallet.balance).toLocaleString("en-GH", { minimumFractionDigits: 2 })}
              </h1>

              {Number(data.wallet.escrow_balance) > 0 && (
                <div className="flex items-center gap-2 bg-black/20 rounded-xl p-3 mb-6 backdrop-blur-sm border border-white/10">
                  <Lock className="w-4 h-4 text-amber-300" />
                  <span className="text-sm font-medium text-white/90">
                    ₵{Number(data.wallet.escrow_balance).toFixed(2)} in Escrow (Active Rides)
                  </span>
                </div>
              )}

              <div className="flex gap-3 mt-6">
                {user?.role === "passenger" && (
                  <Button 
                    onClick={handleTopUp} 
                    disabled={isProcessing}
                    className="flex-1 bg-yenko-yellow hover:bg-yellow-400 text-gray-900 h-14 rounded-2xl font-bold text-lg shadow-lg"
                  >
                    {isProcessing ? <Loader2 className="w-6 h-6 animate-spin" /> : <><Plus className="w-5 h-5 mr-2" /> Top Up</>}
                  </Button>
                )}
                {user?.role === "driver" && (
                  <Button 
                    onClick={handleWithdraw}
                    disabled={isProcessing || data.wallet.balance < 10}
                    className="flex-1 bg-white hover:bg-gray-100 text-yenko-blue h-14 rounded-2xl font-bold text-lg shadow-lg"
                  >
                    {isProcessing ? <Loader2 className="w-6 h-6 animate-spin" /> : <><ArrowUpRight className="w-5 h-5 mr-2" /> Withdraw</>}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Transactions List */}
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4 px-1">Recent Transactions</h2>
            
            {data.transactions.length === 0 ? (
              <div className="text-center py-10 bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200">
                <p className="text-gray-500 font-medium">No transactions yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {data.transactions.map((tx) => (
                  <div key={tx.id} className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                        tx.type === 'deposit' ? 'bg-green-50' : 
                        tx.type === 'escrow_hold' ? 'bg-amber-50' : 
                        'bg-red-50'
                      }`}>
                        {getTransactionIcon(tx.type)}
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">{tx.description || tx.type.replace('_', ' ').toUpperCase()}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-xs text-gray-500 font-medium whitespace-nowrap">
                            {new Date(tx.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                          <span className="text-gray-300">•</span>
                          <span className="flex items-center gap-1 text-xs font-semibold uppercase">
                            {getStatusIcon(tx.status)}
                            <span className={tx.status === 'completed' ? 'text-green-600' : tx.status === 'pending' ? 'text-amber-600' : 'text-red-600'}>
                              {tx.status}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold text-lg ${
                        ['deposit', 'escrow_release'].includes(tx.type) ? 'text-green-600' : 'text-gray-900'
                      }`}>
                        {['deposit', 'escrow_release'].includes(tx.type) ? '+' : '-'}₵{Number(tx.amount).toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      <BottomNav />
    </div>
  );
}
